import React, { useMemo } from 'react';
import type { RetrievalJobDoc, RetrievalJobEventVM } from '../hooks/useRetrievalJobTimeline';
import styles from './RetrievalJobTimeline.module.css';

type Props = {
  job: RetrievalJobDoc | null;
  events: RetrievalJobEventVM[];
  /** When true, show a compact “still running” hint at the bottom */
  active: boolean;
};

/**
 * Operational timeline under a user message (real Firestore steps, not fabricated chain-of-thought).
 */
export function RetrievalJobTimeline({ job, events, active }: Props) {
  const lines = useMemo(() => {
    const out: { key: string; status: 'done' | 'run' | 'info' | 'err'; text: string; durationMs?: number }[] = [];
    const openStepIndex = new Map<string, number>();
    for (const ev of events) {
      if (ev.type === 'step_started' && ev.step) {
        openStepIndex.set(ev.step, out.length);
        out.push({ key: ev.id, status: 'run', text: ev.label, durationMs: ev.durationMs });
      } else if (ev.type === 'step_finished' && ev.step) {
        const idx = openStepIndex.get(ev.step);
        if (idx !== undefined) {
          out[idx] = { key: ev.id, status: 'done', text: ev.label, durationMs: ev.durationMs };
          openStepIndex.delete(ev.step);
        } else {
          out.push({ key: ev.id, status: 'done', text: ev.label, durationMs: ev.durationMs });
        }
      } else if (ev.type === 'error') {
        out.push({ key: ev.id, status: 'err', text: ev.label, durationMs: ev.durationMs });
      } else if (ev.type === 'info') {
        out.push({ key: ev.id, status: 'info', text: ev.label, durationMs: ev.durationMs });
      }
    }
    return out;
  }, [events]);

  const elapsedSec = useMemo(() => {
    if (!job?.createdAt) return null;
    return Math.max(0, Math.round((Date.now() - job.createdAt.getTime()) / 1000));
  }, [job?.createdAt]);

  const progressPct =
    job?.status === 'completed'
      ? 100
      : job?.progress != null
        ? Math.round(Math.min(1, Math.max(0, job.progress)) * 100)
        : null;

  if (!job && events.length === 0 && !active) {
    return null;
  }

  return (
    <div className={styles.wrap} aria-live="polite">
      {(progressPct !== null || job?.status === 'failed') && (
        <div className={styles.metaRow}>
          {progressPct !== null && job?.status !== 'failed' && (
            <div className={styles.progressTrack} aria-hidden>
              <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>
          )}
          <div className={styles.summaryRow}>
            {job?.clientMode && <span className={styles.summaryTag}>Mode: {job.clientMode}</span>}
            {job?.model && <span className={styles.summaryTag}>Model: {job.model}</span>}
            {elapsedSec !== null && <span className={styles.summaryTag}>Elapsed: {elapsedSec}s</span>}
          </div>
          {job?.status === 'failed' && (
            <span className={styles.failed}>Something went wrong</span>
          )}
        </div>
      )}
      {lines.length > 0 && (
        <ul className={styles.list}>
          {lines.map((line) => (
            <li
              key={line.key}
              className={
                line.status === 'done'
                  ? styles.lineDone
                  : line.status === 'run'
                    ? styles.lineRun
                    : line.status === 'err'
                      ? styles.lineErr
                      : styles.lineInfo
              }
            >
              <span className={styles.marker} aria-hidden>
                {line.status === 'done' ? '✓' : line.status === 'run' ? '›' : line.status === 'err' ? '!' : '✓'}
              </span>
              <span className={styles.lineText}>{line.text}</span>
              {typeof line.durationMs === 'number' && line.durationMs > 0 && (
                <span className={styles.lineDuration}>{Math.round(line.durationMs)} ms</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {active && job?.status === 'running' && lines.length === 0 && (
        <p className={styles.waiting}>Starting…</p>
      )}
    </div>
  );
}
